import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to error tracking service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    if (window.errorTracking) {
      window.errorTracking.logError(error, 'error_boundary', {
        componentStack: errorInfo.componentStack,
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  handleNavigate = (path) => {
    window.location.href = path;
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onNavigate={this.handleNavigate}
        />
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

function ErrorFallback({ error, errorInfo, onReset, onNavigate }) {
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom color="error">
          Oops! Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          We&apos;re sorry, but something unexpected happened. Our team has been notified and we&apos;re working to fix it.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => onNavigate('/')}
          >
            Go to Home
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onReset}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={() => onNavigate('/contact')}
          >
            Contact Support
          </Button>
        </Box>

        {isDevelopment && error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              textAlign: 'left',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Error Details (Development Only):
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                overflow: 'auto',
                maxHeight: '300px',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            >
              {error.toString()}
              {errorInfo && errorInfo.componentStack}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          If this problem persists, please{' '}
          <Button
            variant="text"
            size="small"
            onClick={() => onNavigate('/contact')}
            sx={{ textTransform: 'none' }}
          >
            contact our support team
          </Button>
          {' '}and we&apos;ll help you resolve it.
        </Typography>
      </Paper>
    </Container>
  );
}

ErrorFallback.propTypes = {
  error: PropTypes.instanceOf(Error),
  errorInfo: PropTypes.shape({
    componentStack: PropTypes.string,
  }),
  onReset: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

export default ErrorBoundary;

